// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import { PropTypes } from 'prop-types';
import { setSessionStorage, getSessionStorage } from '@tcp/core/src/utils/utils.web';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import {
  BRAND_BASED_BANNER_ICON_DISPLAY,
  DOWNLOAD_APP_CLICK_EVENT_DATA,
  BRAND_BASED_BANNER_DESC_DISPLAY,
  BRAND_BASED_BANNER_TITLE_DISPLAY,
} from '@tcp/core/src/constants/brandSpecific.constants';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '@tcp/core/src/utils/labels/labelUtilty';
import {
  GLOBAL_GENERIC_LABELS,
  GlobalGenericLabels,
} from '@tcp/core/src/utils/labels/globalGeneric.labels';

import { getIconPath, isCanada, parseBoolean, getBrandInCaps } from '@tcp/core/src/utils';
import style, {
  InfoWrapper,
  Title,
  Description,
  ButtonWrapper,
  Container,
  CloseButton,
  ViewButton,
  ViewButtonText,
  Wrapper,
  Icon,
} from '../styles/AppBanner.style';
import appBannerConfig from '../config';
import { getAppInstallPageUrl } from '../AppBanner.utils';
import { GLOBAL_SMART_BANNER_LABELS, AppBannerLabels } from '../labels/AppBanner.labels';

const bannerSessionClose = appBannerConfig.SMART_BANNER_SESSION_CLOSE;

/**
 * @description
 * Display Rules:
 * The banner will auto display on the homepage or the first page the user lands on when they reach the website
 * If the first page the user sees is not the homepage then the banner should display on page load of that first page only and the remaining display rules apply
 * The banner will not display on Bag and Checkout pages
 * The banner will live on remaining pages of the website but will only display if the user scrolls up to the top of the screen (not on page load)
 * If the user actively closes the banner, it should no longer display at all in the users' current session
 * The banner should redisplay with a fresh session
 */
class AppBanner extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showBanner: props.isShowAppBannerKillSwitch && !isCanada(),
    };

    this.globalGenericLabels = getLabelFromCopyOrFromTranslation(
      GLOBAL_GENERIC_LABELS,
      GlobalGenericLabels
    );

    this.smartBannerLabels = getLabelFromCopyOrFromTranslation(
      GLOBAL_SMART_BANNER_LABELS,
      AppBannerLabels
    );
    this.viewButtonLabel = renderLabel(
      this.smartBannerLabels.lbl_smart_banner_button,
      'lbl_smart_banner_button'
    );
  }

  getTitleLabelVal = (labelKey) => renderLabel(this.globalGenericLabels[labelKey], labelKey);

  getDescLabelVal = (labelKey) => renderLabel(this.smartBannerLabels[labelKey], labelKey);

  componentDidMount() {
    this.canRenderBanner();
  }

  componentDidUpdate(prevProps) {
    const { asPath: prevAsPath } = prevProps;
    const { asPath } = this.props;
    if (prevAsPath !== asPath) {
      this.reInitializeAppBanner();
    }
  }

  /**
   * @method reInitializeAppBanner
   * @description hide banner from load and then decides based on display rules
   */
  reInitializeAppBanner = () => {
    this.setState({
      showBanner: false,
    });
    // Intermittently, banner was not hiding on subsequest page visits, removed set state callback and added timeout fixed the issue.
    const renderBannerTimeout = setTimeout(() => {
      this.canRenderBanner();
      clearTimeout(renderBannerTimeout);
    }, 0);
  };

  canRenderBanner = () => {
    if (!isCanada()) {
      const { isShowAppBannerKillSwitch } = this.props;
      const { SMART_BANNER_PAGELOAD_DONE, SMART_BANNER_SESSION_CLOSE } = appBannerConfig;
      const isBannerLoaded = parseBoolean(getSessionStorage(SMART_BANNER_PAGELOAD_DONE));

      if (isShowAppBannerKillSwitch) {
        const isSessionClose = parseBoolean(getSessionStorage(SMART_BANNER_SESSION_CLOSE));

        if (isSessionClose || isBannerLoaded) {
          this.setState({
            showBanner: false,
          });
        }
        if (isBannerLoaded) {
          this.showBannerOnScrollUp(this, isSessionClose);
        } else {
          setSessionStorage({ key: SMART_BANNER_PAGELOAD_DONE, value: true });
        }
      }
    }
  };

  /**
   * @method showBannerOnScrollUp
   * @description show banner on scroll up from pages other than home pages
   */
  showBannerOnScrollUp = (component, isSessionClose) => {
    if (window) {
      let prevScrollpos = window.pageYOffset;
      // on PDP and other pages we have auto scroll, which is making windows scoll event to call. To avoid for banner
      // added bannerScroll flag
      let bannerScroll = true;
      const { isBrowsePageAndAnchored } = this.props;
      window.onscroll = () => {
        const currentScrollPos = window.pageYOffset;
        if (
          prevScrollpos > currentScrollPos &&
          bannerScroll &&
          (!isBrowsePageAndAnchored || (isBrowsePageAndAnchored && currentScrollPos < 200))
        ) {
          component.setState({
            showBanner: !isSessionClose,
          });
          bannerScroll = false;
        }
        prevScrollpos = currentScrollPos;
      };
    }
  };

  /**
   * This function will be used to close the smart banner
   */
  close = () => {
    this.setState(
      {
        showBanner: false,
      },
      () => setSessionStorage({ key: bannerSessionClose, value: true })
    );
  };

  /**
   * This will be used to redirect user to android play store
   */
  install = () => {
    const { setClickAnalyticsData, trackClick } = this.props;
    const brand = getBrandInCaps();
    const appNameCustomEvents = DOWNLOAD_APP_CLICK_EVENT_DATA[brand];
    setClickAnalyticsData({
      customEvents: appNameCustomEvents,
      eventName: 'download promo: header',
      appDownloadLocation: 'Header',
    });
    trackClick();
    const analyticsTimer = setTimeout(() => {
      setClickAnalyticsData({});
      window.location = getAppInstallPageUrl();
      clearTimeout(analyticsTimer);
    }, 100);
  };

  render() {
    const { className, asPath } = this.props;
    const { showBanner } = this.state;
    if (!showBanner) {
      return null;
    }
    const showAppBanner = asPath.toString().indexOf('hideHeaderFooter=true') > -1;
    const brand = getBrandInCaps();
    const icon = getIconPath(BRAND_BASED_BANNER_ICON_DISPLAY[brand]);
    const iconStyle = {
      backgroundImage: `url(${icon})`,
    };
    const titleLabel = this.getTitleLabelVal(BRAND_BASED_BANNER_TITLE_DISPLAY[brand]);
    const descLabel = this.getDescLabelVal(BRAND_BASED_BANNER_DESC_DISPLAY[brand]);

    return (
      <div className={`${className} hide-on-desktop hide-on-tablet hide-on-native-web-view`}>
        <Wrapper className={`app-banner-wrapper ${showAppBanner ? 'hide-banner' : ''}`}>
          <Container>
            <CloseButton type="button" aria-label="close" onClick={this.close}>
              &times;
            </CloseButton>
            <Icon style={iconStyle} />
            <InfoWrapper>
              <Title>{titleLabel}</Title>
              <Description>{descLabel}</Description>
            </InfoWrapper>
            <ButtonWrapper>
              <ViewButton onClick={this.install}>
                <ViewButtonText>{this.viewButtonLabel}</ViewButtonText>
              </ViewButton>
            </ButtonWrapper>
          </Container>
        </Wrapper>
      </div>
    );
  }
}

AppBanner.defaultProps = {
  className: '',
  asPath: '',
  isShowAppBannerKillSwitch: false,
  setClickAnalyticsData: () => {},
  trackClick: () => {},
  isBrowsePageAndAnchored: false,
};

AppBanner.propTypes = {
  className: PropTypes.string,
  asPath: PropTypes.string,
  isShowAppBannerKillSwitch: PropTypes.bool,
  setClickAnalyticsData: PropTypes.func,
  trackClick: PropTypes.func,
  isBrowsePageAndAnchored: PropTypes.bool,
};

export default withStyles(AppBanner, style);
export { AppBanner as AppBannerVanilla };
